import {
  WORKBOOK_MAX_SCHEMA_GROUP_MEMBERS,
  WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS,
  workbookSchemaGroupLinkIds,
  type WorkbookSource,
} from '@erp/shared-types';

type SchemaGroupSource = Extract<WorkbookSource, { kind: 'schema-group' }>;

/** A Schema Definition and how many readable Data Sources share its columns. */
export interface SchemaGroup {
  schemaId: string;
  schemaName: string;
  linkCount: number;
  /** Every readable Data Source of the schema, in name order. */
  members: { id: string; name: string }[];
}

/** The schemas the server says can be combined, by schema name. */
export function groupSchemas(
  schemas: readonly {
    id: string;
    name: string;
    linkCount: number;
    members: { id: string; name: string }[];
  }[],
): SchemaGroup[] {
  return schemas
    .flatMap((schema) =>
      schema.linkCount >= WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS
        ? [
            {
              schemaId: schema.id,
              schemaName: schema.name,
              linkCount: schema.linkCount,
              members: schema.members,
            },
          ]
        : [],
    )
    .sort((a, b) => a.schemaName.localeCompare(b.schemaName));
}

/**
 * The group's members: every Data Source of the schema less the left out, so a
 * feed added later joins ("auto"), or just the ones chosen now, in `links` order.
 */
export function schemaGroupMembers(
  links: readonly { id: string }[],
  excluded: readonly string[],
  auto: boolean,
): SchemaGroupSource['members'] {
  const left = new Set(excluded);
  if (!auto)
    return {
      mode: 'pick',
      linkIds: links.flatMap((link) => (left.has(link.id) ? [] : [link.id])),
    };
  return excluded.length
    ? { mode: 'all', exclude: [...excluded] }
    : { mode: 'all' };
}

/** Whether `members` can be added: a fixed list needs two to the most; all, one. */
export function schemaGroupMembersValid(
  members: SchemaGroupSource['members'],
): boolean {
  if (members.mode === 'all') return true;
  return (
    members.linkIds.length >= WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS &&
    members.linkIds.length <= WORKBOOK_MAX_SCHEMA_GROUP_MEMBERS
  );
}

/** What a schema group Source reads, as the catalog names it, for showing it. */
export interface SchemaGroupReading {
  schemaName: string | undefined;
  /** Every Data Source of the schema (less `excluded`), not a fixed list. */
  all: boolean;
  excluded: number;
  members: string[];
  /** The link id of each member, in step with `members` (unique, for keys). */
  memberIds: string[];
  /** Whether the schema's complete membership is known (else only its name shows). */
  resolved: boolean;
}

export function schemaGroupReading(
  source: SchemaGroupSource,
  /** What is known of the schema: its name and its complete membership, once read. */
  schemaGroup: {
    schemaName?: string;
    links?: readonly { id: string; name: string }[];
  },
  /** Names a pick whose Data Source is gone or not yet known, so no id shows. */
  unknownName: string,
): SchemaGroupReading {
  const names = new Map(
    (schemaGroup.links ?? []).map((link) => [link.id, link.name]),
  );
  const ids = workbookSchemaGroupLinkIds(
    source.members,
    (schemaGroup.links ?? []).map((link) => link.id),
  );
  return {
    schemaName: schemaGroup.schemaName,
    all: source.members.mode === 'all',
    excluded:
      source.members.mode === 'all' ? (source.members.exclude?.length ?? 0) : 0,
    memberIds: ids,
    resolved: schemaGroup.links !== undefined,
    members: ids.map(
      (id) => source.labels?.[id] ?? names.get(id) ?? unknownName,
    ),
  };
}
